
'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Link, usePathname, useRouter } from '@/navigation';

export function Navbar() {
  const t = useTranslations('nav');
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredPath, setHoveredPath] = useState(pathname);
  const [scrolled, setScrolled] = useState(false);

  const links = [
    { name: t('home'), href: '/' },
    { name: t('projects'), href: '/projects' },
    { name: t('pricing'), href: '/pricing' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
  }, [isOpen]);

  const headerClasses = scrolled
    ? 'bg-black/70 backdrop-blur-md border-b border-white/10'
    : 'bg-transparent border-b border-transparent';

  return (
    <>
      <header className={`hidden md:flex fixed top-0 left-0 right-0 z-50 w-full h-20 items-center justify-between transition-[background-color,border-color,backdrop-filter] duration-300 transform-gpu ${headerClasses}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
            <Link href="/" className="flex flex-col group relative transform-gpu">
              <span className="text-xl font-bold tracking-tight text-white group-hover:opacity-70 transition-opacity">HVN</span>
              <span className="text-[8px] font-mono tracking-[0.3em] text-gray-400 -mt-1 uppercase">DEVELOPERS</span>
            </Link>

          <nav 
            className="flex items-center gap-2 bg-black/30 border border-white/10 rounded-full p-2 transform-gpu"
            onMouseLeave={() => setHoveredPath(pathname)}
          >
            {links.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onMouseEnter={() => setHoveredPath(link.href)}
                className={`relative px-4 py-2 text-sm transition-colors rounded-full ${
                  pathname === link.href ? 'text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                {hoveredPath === link.href && (
                  <motion.span
                    layoutId="hoverIndicator"
                    className="absolute inset-0 bg-white/10 rounded-full z-0"
                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                  />
                )}
                <span className="relative z-10 whitespace-nowrap">{link.name}</span>
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <motion.div
              className="transform-gpu"
              whileHover={{ scale: 1.03, boxShadow: "0 0 20px rgba(255,255,255,0.15)" }}
              whileTap={{ scale: 0.98 }}
            >
              <Link
                href="/#contact"
                className="flex items-center justify-center rounded-full px-6 py-2.5 bg-white text-black text-sm font-semibold hover:bg-gray-200 transition-colors"
              >
                {t('cta')}
              </Link>
            </motion.div>
          </div>
        </div>
      </header>
      
      <header className={`md:hidden fixed top-0 left-0 right-0 z-50 w-full h-20 flex items-center justify-between transition-[background-color,border-color,backdrop-filter] duration-300 transform-gpu ${isOpen || scrolled ? 'bg-black/70 backdrop-blur-md border-b border-white/10' : 'bg-transparent border-b border-transparent'}`}>
        <div className="container mx-auto px-6 flex justify-between items-center">
          <Link href="/" className="flex flex-col group relative z-50">
            <span className="text-xl font-bold tracking-tight text-white group-hover:opacity-70 transition-opacity">HVN</span>
            <span className="text-[8px] font-mono tracking-[0.3em] text-gray-400 -mt-1 uppercase">DEVELOPERS</span>
          </Link>

          <motion.button 
            onClick={() => setIsOpen(!isOpen)}
            className="z-50 text-white transform-gpu"
            animate={{ rotate: isOpen ? 90 : 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </header>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-lg z-40 flex flex-col items-center justify-center md:hidden"
          >
            <nav className="flex flex-col items-center gap-8 mb-16">
              {[...links, { name: t('contact'), href: '/#contact' }].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  onClick={() => setIsOpen(false)}
                  className={`text-3xl font-medium transition-colors ${
                    pathname === link.href ? 'text-white' : 'text-gray-500 hover:text-white'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <div className="flex flex-col items-center gap-8">
              <LanguageSwitcher />
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
              >
                <Link
                  href="/#contact"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-center rounded-full px-8 py-4 bg-white text-black font-semibold hover:bg-gray-200 transition-colors"
                >
                  {t('cta')}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const { locale } = router;

  const languages = [
    { code: 'uz', name: 'UZ' },
    { code: 'en', name: 'EN' },
    { code: 'ru', name: 'RU' },
  ];

  const handleLangChange = (newLocale: string) => {
    router.replace(pathname, { locale: newLocale });
  };

  return (
    <div className="flex items-center gap-1 rounded-full border border-white/10 p-1 bg-black/30 transform-gpu">
      {languages.map((lang) => (
        <button
          key={lang.code}
          onClick={() => handleLangChange(lang.code)}
          className={`relative px-3 py-1 text-sm rounded-full transition-colors ${
            locale === lang.code ? 'text-black' : 'text-white hover:text-white/80'
          }`}
        >
          {locale === lang.code && (
            <motion.span
              layoutId="activeLang"
              className="absolute inset-0 bg-white rounded-full z-0"
              transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            />
          )}
          <span className="relative z-10">{lang.name}</span>
        </button>
      ))}
    </div>
  );
}
